/**
 * HID barcode parser.
 * Converts USB HID keyboard reports (from qz.hid callbacks) into barcode strings.
 * Uses standard USB HID Usage Table (page 0x07) for keycode → character mapping.
 */

// USB HID Keyboard Usage Table — keycodes 0x04..0x38
// Unshifted characters (lowercase/number)
const KEYCODE_MAP = {
  0x04: 'a', 0x05: 'b', 0x06: 'c', 0x07: 'd', 0x08: 'e',
  0x09: 'f', 0x0A: 'g', 0x0B: 'h', 0x0C: 'i', 0x0D: 'j',
  0x0E: 'k', 0x0F: 'l', 0x10: 'm', 0x11: 'n', 0x12: 'o',
  0x13: 'p', 0x14: 'q', 0x15: 'r', 0x16: 's', 0x17: 't',
  0x18: 'u', 0x19: 'v', 0x1A: 'w', 0x1B: 'x', 0x1C: 'y',
  0x1D: 'z', 0x1E: '1', 0x1F: '2', 0x20: '3', 0x21: '4',
  0x22: '5', 0x23: '6', 0x24: '7', 0x25: '8', 0x26: '9',
  0x27: '0', 0x2D: '-', 0x2E: '=', 0x2F: '[', 0x30: ']',
  0x31: '\\', 0x33: ';', 0x34: "'", 0x35: '`', 0x36: ',',
  0x37: '.', 0x38: '/',
};

// Shifted characters (uppercase/symbols)
const KEYCODE_MAP_SHIFTED = {
  0x04: 'A', 0x05: 'B', 0x06: 'C', 0x07: 'D', 0x08: 'E',
  0x09: 'F', 0x0A: 'G', 0x0B: 'H', 0x0C: 'I', 0x0D: 'J',
  0x0E: 'K', 0x0F: 'L', 0x10: 'M', 0x11: 'N', 0x12: 'O',
  0x13: 'P', 0x14: 'Q', 0x15: 'R', 0x16: 'S', 0x17: 'T',
  0x18: 'U', 0x19: 'V', 0x1A: 'W', 0x1B: 'X', 0x1C: 'Y',
  0x1D: 'Z', 0x1E: '!', 0x1F: '@', 0x20: '#', 0x21: '$',
  0x22: '%', 0x23: '^', 0x24: '&', 0x25: '*', 0x26: '(',
  0x27: ')', 0x2D: '_', 0x2E: '+', 0x2F: '{', 0x30: '}',
  0x31: '|', 0x33: ':', 0x34: '"', 0x35: '~', 0x36: '<',
  0x37: '>', 0x38: '?',
};

const ENTER_KEYCODE = 0x28;
const LEFT_SHIFT = 0x02;
const RIGHT_SHIFT = 0x20;

/**
 * Parse a single HID keyboard report into a character.
 * Standard 8-byte HID report: [modifier, reserved, key1, key2, key3, key4, key5, key6]
 * @param {number[]} bytes - Array of byte values from HID report
 * @returns {{ char: string|null, isEnter: boolean }}
 */
export function parsearReporteHid(bytes) {
  if (!bytes || bytes.length < 3) return { char: null, isEnter: false };

  const modifier = bytes[0];
  const keycode = bytes[2];

  if (keycode === 0) return { char: null, isEnter: false };
  if (keycode === ENTER_KEYCODE) return { char: null, isEnter: true };

  const shifted = (modifier & LEFT_SHIFT) !== 0 || (modifier & RIGHT_SHIFT) !== 0;
  const map = shifted ? KEYCODE_MAP_SHIFTED : KEYCODE_MAP;
  const char = map[keycode] || null;

  return { char, isEnter: false };
}

/**
 * Create a barcode accumulator that collects HID reports into complete barcodes.
 * Emits the barcode string when Enter (0x28) is received.
 * Discards incomplete scans after 500ms timeout.
 * @param {(barcode: string) => void} onBarcode - Callback when a complete barcode is received
 * @returns {(data: any) => void} - Function to feed HID callback data into
 */
export function crearAcumuladorBarcode(onBarcode) {
  let buffer = '';
  let timeoutId = null;

  const reset = () => {
    buffer = '';
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return (data) => {
    // qz.hid callbacks send data as hex string arrays or byte arrays
    let bytes;
    if (Array.isArray(data)) {
      bytes = data.map(b => typeof b === 'string' ? parseInt(b, 16) : b);
    } else if (typeof data === 'string') {
      // Hex string like "00002800000000"
      bytes = [];
      for (let i = 0; i < data.length; i += 2) {
        bytes.push(parseInt(data.substring(i, i + 2), 16));
      }
    } else {
      return;
    }

    const { char, isEnter } = parsearReporteHid(bytes);

    if (isEnter && buffer.length > 0) {
      onBarcode(buffer);
      reset();
      return;
    }

    if (char) {
      buffer += char;
      // Reset timeout on each character — discard after 500ms of inactivity
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (buffer.length > 0) {
          console.warn('[HID Barcode] Timeout: descartando scan incompleto:', buffer);
        }
        reset();
      }, 500);
    }
  };
}
